import { expandArchiveRow, uploadArchiveBytes } from './batch';
import { replaceRow, withProgress, withUploadError, withUploaded } from './rows';
/**
 * Re-drive exactly one row, addressed by id. Every other row's attachmentId,
 * jobId and state is untouched — `replaceRow` only ever rebuilds the one
 * matching element.
 *
 * Two failure classes, two strategies:
 *   1. Upload failed before any processing job existed — re-drive the
 *      upload against the cached File blob.
 *   2. Processing failed after a successful upload — call RetryProcessing
 *      so reverie re-runs the same artifact bytes; no re-upload needed.
 */ export async function retryRow(rows, rowId, context, deps, onRowsChange) {
    const target = rows.find((r)=>r.id === rowId);
    if (!target) return {
        ok: false,
        rows,
        error: 'Row not found',
        kind: 'upload'
    };
    if (target.sourceDownloadPending) {
        return {
            ok: false,
            rows,
            error: 'Download the source before uploading it.',
            kind: 'upload'
        };
    }
    if (target.status === 'uploaded' && target.attachmentId && target.processingStatus === 'error') {
        let current = replaceRow(rows, rowId, (r)=>({
                ...r,
                processingStatus: 'processing'
            }));
        onRowsChange(current);
        try {
            const resp = target.jobId ? await deps.retryProcessing(target.jobId) : await deps.startProcessing?.(target.attachmentId);
            const newJobId = resp?.jobId ?? null;
            current = replaceRow(current, rowId, (r)=>({
                    ...r,
                    jobId: newJobId,
                    processingStatus: newJobId ? 'processing' : 'error',
                    statusLabel: newJobId ? undefined : 'Processing could not start. Retry or remove this file.'
                }));
            onRowsChange(current);
            return newJobId ? {
                ok: true,
                rows: current
            } : {
                ok: false,
                rows: current,
                error: 'Processing could not start. Please retry.',
                kind: 'processing'
            };
        } catch (err) {
            current = replaceRow(current, rowId, (r)=>({
                    ...r,
                    processingStatus: 'error'
                }));
            onRowsChange(current);
            return {
                ok: false,
                rows: current,
                error: err instanceof Error ? err.message : 'Failed to retry processing',
                kind: 'processing'
            };
        }
    }
    // An archive whose bytes already uploaded but whose ExpandArchiveArtifact call failed
    // (RPC error, or zero usable children) — re-expand against the cached artifactId.
    // Re-uploading would mint a second archive artifact and orphan the first.
    if (target.isArchive && target.attachmentId) {
        const expandResult = await expandArchiveRow(rows, target.id, target.file.name, target.attachmentId, target.useDeepProcessing, target.outputFormat, deps.expandArchive, onRowsChange);
        if (expandResult.ok) return {
            ok: true,
            rows: expandResult.rows,
            skipped: expandResult.skipped
        };
        return {
            ok: false,
            rows: expandResult.rows,
            error: `Failed to unpack ${target.file.name}`,
            kind: 'expand',
            skipped: expandResult.skipped
        };
    }
    // An archive whose upload itself failed — re-drive the upload, then expand same as a
    // first-time submit, since the archive was never sent to processing as-is.
    if (target.isArchive) {
        const uploadResult = await uploadArchiveBytes(rows, target, context, deps.transport, onRowsChange);
        if (!uploadResult.ok) {
            return {
                ok: false,
                rows: uploadResult.rows,
                error: `Failed to upload ${target.file.name}`,
                kind: 'upload'
            };
        }
        const expandResult = await expandArchiveRow(uploadResult.rows, target.id, target.file.name, uploadResult.artifactId, target.useDeepProcessing, target.outputFormat, deps.expandArchive, onRowsChange);
        if (expandResult.ok) return {
            ok: true,
            rows: expandResult.rows,
            skipped: expandResult.skipped
        };
        return {
            ok: false,
            rows: expandResult.rows,
            error: `Failed to unpack ${target.file.name}`,
            kind: 'expand',
            skipped: expandResult.skipped
        };
    }
    let current = replaceRow(rows, rowId, (r)=>({
            ...r,
            status: 'uploading',
            progress: 0,
            processingStatus: 'none',
            jobId: null
        }));
    onRowsChange(current);
    try {
        const result = await deps.transport(target.file, {
            tenantId: context.tenantId,
            ownerUserId: context.ownerUserId,
            useDeepProcessing: target.useDeepProcessing,
            outputFormat: target.outputFormat,
            onProgress: (percent)=>{
                current = replaceRow(current, rowId, (r)=>withProgress(r, percent));
                onRowsChange(current);
            }
        });
        current = replaceRow(current, rowId, (r)=>withUploaded(r, result));
        onRowsChange(current);
        return {
            ok: true,
            rows: current
        };
    } catch  {
        current = replaceRow(current, rowId, withUploadError);
        onRowsChange(current);
        return {
            ok: false,
            rows: current,
            error: 'Upload failed',
            kind: 'upload'
        };
    }
}
